import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../api";

/**
 * Admin → Market data (V018): CoStar markets and submarkets for Trends.
 * Create the area as CoStar names it, link the client's hotels, and upload
 * the monthly Data-tab export (.xlsx). A re-upload replaces the months it covers.
 */
interface Area {
  id: string; name: string; level: "market" | "submarket" | "national"; parentId: string | null; lastUploadAt: string | null; lastFileName: string | null;
  latest: string | null; earliest: string | null; hotelIds: string[];
}
interface Hotel { id: string; name: string; code: string | null; active: boolean }

export function MarketData({ clientId }: { clientId: string }) {
  const qc = useQueryClient();
  const areas = useQuery({ queryKey: ["market-areas", clientId], queryFn: () => api<{ enabled: boolean; areas: Area[] }>(`/api/admin/market/areas?clientId=${clientId}`) });
  const hotels = useQuery({ queryKey: ["admin-hotels", clientId], queryFn: () => api<{ hotels: Hotel[] }>(`/api/admin/hotels?clientId=${clientId}`) });
  const [name, setName] = useState(""); const [level, setLevel] = useState<"market" | "submarket" | "national">("market"); const [parentId, setParentId] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["market-areas", clientId] });
  const create = useMutation({
    mutationFn: () => api("/api/admin/market/areas", { method: "POST", body: { clientId, name, level, parentId: level === "submarket" ? parentId || null : null } }),
    onSuccess: () => { setName(""); setMsg(null); refresh(); }, onError: (e) => setMsg((e as Error).message),
  });
  const markets = (areas.data?.areas ?? []).filter((a) => a.level === "market");

  if (areas.isLoading) return <div className="card muted">Loading…</div>;
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Market data (CoStar)</h3>
      {!areas.data?.enabled && <p className="error">This client isn't licensed for market data, so its users won't see it on Trends.</p>}
      <p className="muted" style={{ fontSize: 13 }}>
        In CoStar: open the market or submarket → Data tab → set the period to Monthly → Export (.xlsx). Upload it here once a month.
        Months after the latest actual are CoStar's forecast and show dashed on Trends.
        For the U.S. hotel cycle on Trends, add one <strong>National</strong> area and upload the United States export the same way
        (until then Trends uses U.S. accommodation employment as a stand-in).
      </p>
      <div className="row" style={{ marginBottom: 12 }}>
        <input placeholder='Name as in CoStar, e.g. "Tampa Bay, FL"' value={name} onChange={(e) => setName(e.target.value)} style={{ minWidth: 260 }} />
        <select value={level} onChange={(e) => setLevel(e.target.value as "market" | "submarket" | "national")}><option value="market">Market</option><option value="submarket">Submarket</option><option value="national">National (U.S.)</option></select>
        {level === "submarket" && (
          <select value={parentId} onChange={(e) => setParentId(e.target.value)}>
            <option value="">Parent market…</option>{markets.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        )}
        <button className="btn" disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>Add</button>
      </div>
      {msg && <p className="error">{msg}</p>}
      {(areas.data?.areas ?? []).length === 0 ? <p className="muted">No markets yet.</p> : (
        <table>
          <thead><tr><th>Area</th><th>Months on file</th><th>Hotels</th><th>Last upload</th><th></th></tr></thead>
          <tbody>{areas.data!.areas.map((a) => (
            <AreaRow key={a.id} a={a} parent={markets.find((m) => m.id === a.parentId)?.name ?? null} hotels={(hotels.data?.hotels ?? []).filter((h) => h.active)} onChange={refresh} />
          ))}</tbody>
        </table>
      )}
    </div>
  );
}

function AreaRow({ a, parent, hotels, onChange }: { a: Area; parent: string | null; hotels: Hotel[]; onChange: () => void }) {
  const [editing, setEditing] = useState(false);
  const [picked, setPicked] = useState<string[]>(a.hotelIds);
  const [note, setNote] = useState<string | null>(null);
  const run = useMutation({ mutationFn: (f: () => Promise<string | null>) => f(), onSuccess: (m) => { setNote(m); onChange(); }, onError: (e) => setNote((e as Error).message) });
  const upload = (file: File) => run.mutate(async () => {
    const fd = new FormData(); fd.append("file", file);
    const r = await fetch(`/api/admin/market/areas/${a.id}/upload`, { method: "POST", credentials: "same-origin", headers: { "x-owners-app": "1" }, body: fd });
    const d = r.headers.get("content-type")?.includes("json") ? await r.json() : null;
    if (!r.ok) throw new ApiError(r.status, d?.error ?? `Upload failed (${r.status})`);
    return `Loaded ${d.months} months, ${d.from} to ${d.to} · latest actual ${d.latestActual ?? "—"}${d.forecastMonths ? ` · ${d.forecastMonths} forecast months` : ""}`;
  });
  const save = () => run.mutate(async () => { await api(`/api/admin/market/areas/${a.id}`, { method: "PATCH", body: { hotelIds: picked } }); setEditing(false); return "Hotels saved"; });
  const remove = () => { if (confirm(`Delete ${a.name} and its data?`)) run.mutate(async () => { await api(`/api/admin/market/areas/${a.id}`, { method: "DELETE" }); return null; }); };
  const names = hotels.filter((h) => a.hotelIds.includes(h.id)).map((h) => h.code ?? h.name);

  return (
    <tr>
      <td><strong>{a.name}</strong><div className="muted" style={{ fontSize: 12 }}>{a.level === "market" ? "Market" : a.level === "national" ? "National · U.S. hotel cycle on Trends" : `Submarket${parent ? ` of ${parent}` : ""}`}</div></td>
      <td>{a.earliest ? `${a.earliest} → ${a.latest ?? "—"}` : <span className="muted">none</span>}</td>
      <td style={{ maxWidth: 320 }}>
        {a.level === "national" ? <span className="muted">All hotels (used for the U.S. cycle)</span> : <>
        {!editing ? <>{names.length ? names.join(", ") : <span className="muted">none</span>} <button className="btn secondary" style={{ padding: "2px 8px" }} onClick={() => { setPicked(a.hotelIds); setEditing(true); }}>Edit</button></> : (
          <div>
            <div style={{ maxHeight: 200, overflowY: "auto", fontSize: 13 }}>
              {hotels.map((h) => (
                <label key={h.id} style={{ display: "block" }}>
                  <input type="checkbox" checked={picked.includes(h.id)} onChange={(e) => setPicked(e.target.checked ? [...picked, h.id] : picked.filter((x) => x !== h.id))} /> {h.code ? `${h.code} · ` : ""}{h.name}
                </label>
              ))}
            </div>
            <div className="row"><button className="btn" onClick={save}>Save</button><button className="btn secondary" onClick={() => setEditing(false)}>Cancel</button></div>
          </div>
        )}
        <div className="muted" style={{ fontSize: 11 }}>A hotel has one {a.level}; linking it here moves it from any other.</div>
        </>}
      </td>
      <td style={{ fontSize: 12 }}>{a.lastUploadAt ? <>{new Date(a.lastUploadAt).toLocaleDateString("en-US")}<div className="muted">{a.lastFileName}</div></> : <span className="muted">never</span>}</td>
      <td>
        <label className="btn secondary" style={{ cursor: "pointer" }}>
          {run.isPending ? "Working…" : "Upload export"}
          <input type="file" accept=".xlsx" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
        </label>
        <button className="btn secondary" onClick={remove} style={{ marginLeft: 6 }}>Delete</button>
        {note && <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{note}</div>}
      </td>
    </tr>
  );
}
